import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Edge-swipe-from-the-left to go back, iOS-style. Listens at the
// document level so any route can trigger it; only acts when:
//   - the touch starts within 24px of the left edge,
//   - the current pathname has a recognised "back" target (not a
//     top-level tab like /home, /calendar, /learning, /profile),
//   - the user crosses the commit threshold (40% of viewport width)
//     OR releases past 90px with rightward velocity.
//
// During the drag we translate the entire React-routed subtree to
// the right so the user can SEE the back action (Apple's
// interactive-pop transition), and fade in a thin shadow on the
// trailing edge so it reads as a sliding card. On commit we fire
// vibrateTap() and call navigate(-1).

import { vibrateTap } from '@/utils/sound';

const TOP_LEVEL = new Set(['/home', '/calendar', '/learning', '/profile']);

export function useEdgeSwipeBack() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (TOP_LEVEL.has(pathname)) return; // top-level tabs aren't pop-able
    if (typeof window === 'undefined') return;

    let startX = 0;
    let startY = 0;
    let active = false;
    let lastX = 0;
    let stage = null; // the route's <div key={pathname}> wrapper we translate
    const COMMIT_RATIO = 0.4;
    const EDGE_PX = 24;
    const VW = () => window.innerWidth;

    function findStage() {
      // The AppRoutes wrapper in src/App.jsx is the first .motion-page*
      // element under <body>. We translate that so the user sees the
      // current route slide right while peeking at whatever the back
      // history will reveal.
      return (
        document.querySelector('.motion-page--forward') ||
        document.querySelector('.motion-page--back') ||
        document.querySelector('.motion-page')
      );
    }

    function reset(animate) {
      if (!stage) return;
      if (animate) {
        stage.style.transition = 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1), box-shadow 200ms ease';
      }
      stage.style.transform = '';
      stage.style.boxShadow = '';
      // Allow the next CSS transition for free interactions
      requestAnimationFrame(() => {
        if (stage) stage.style.transition = '';
      });
    }

    function onTouchStart(e) {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      if (t.clientX > EDGE_PX) return;
      startX = t.clientX;
      startY = t.clientY;
      active = true;
      lastX = startX;
      stage = findStage();
      if (stage) stage.style.transition = 'none';
    }
    function onTouchMove(e) {
      if (!active) return;
      const t = e.touches[0];
      const dx = t.clientX - startX;
      const dy = Math.abs(t.clientY - startY);
      // Cancel if the user is clearly scrolling vertically.
      if (dy > 30 && dy > Math.abs(dx)) {
        active = false;
        reset(true);
        return;
      }
      if (dx <= 0) return;
      lastX = t.clientX;
      if (stage) {
        stage.style.transform = `translateX(${dx}px)`;
        stage.style.boxShadow = `-${Math.min(20, dx / 4)}px 0 32px rgba(0,0,0,${Math.min(0.18, dx / 800)})`;
      }
    }
    function onTouchEnd() {
      if (!active) return;
      const dx = lastX - startX;
      active = false;
      if (dx > VW() * COMMIT_RATIO || dx > 90) {
        // Commit: animate the stage off-screen, then navigate. The
        // routing change unmounts this component which clears the
        // listeners; the new route renders normally.
        if (stage) {
          stage.style.transition = 'transform 220ms cubic-bezier(0.32, 0.72, 0, 1)';
          stage.style.transform = `translateX(${VW()}px)`;
        }
        vibrateTap();
        // navigate after the slide-out so the user perceives one
        // continuous gesture; navigate then unmounts the stage.
        setTimeout(() => navigate(-1), 200);
      } else {
        reset(true);
      }
    }
    function onTouchCancel() {
      if (!active) return;
      active = false;
      reset(true);
    }

    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: true });
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('touchcancel', onTouchCancel);
    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchCancel);
      // Make sure we don't leave a route stranded mid-translate if
      // the component unmounts mid-gesture.
      if (stage) reset(false);
    };
  }, [pathname, navigate]);
}
